import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { GoSearch } from 'react-icons/go';

import { MenuItem, FormInput, Text } from 'modules/common';
import { useOnClickOutside } from 'modules/common/hooks';

function Menu({
  maxHeight,
  options,
  width,
  value,
  isMultiple,
  isGroup,
  onChange,
  isAbsolute,
  disabled,
  isOpen,
  isSearchable,
  name,
  label,
  placeholder,
  searchValue,
  onSearch,
  emptyElement,
  className,
  clickOutsideProps,
  menuItemProps,
  emptyElementClassName,
}) {
  const ref = React.useRef();
  const [hasScroll, setHasScroll] = React.useState(false);

  useOnClickOutside(clickOutsideProps?.ref, clickOutsideProps?.set);

  const handleOnScroll = () => {
    if (ref.current?.scrollHeight > ref.current?.clientHeight) {
      setHasScroll(true);
      return;
    }

    setHasScroll(false);
  };

  const isSelected = (selectedOption, item) => {
    if (Array.isArray(selectedOption)) {
      return (
        selectedOption?.findIndex((x) => `${x?.value}` === `${item?.value}`) >
        -1
      );
    }
    return false;
  };

  const handleOnClick = (item, $isSelected, e) => {
    onChange(item, $isSelected, e);
  };

  React.useEffect(() => {
    if (ref.current?.scrollHeight > ref.current?.clientHeight) {
      setHasScroll(true);
      return;
    }

    setHasScroll(false);
  }, [options, isOpen]);

  const renderList = (list) =>
    list?.map((item, key) => {
      const $isSelected = isSelected(value, item);
      const { className: $className, ...rest } = menuItemProps || {};

      return (
        <MenuItem
          // eslint-disable-next-line react/no-array-index-key
          key={key}
          onClick={(e) => handleOnClick(item, $isSelected, e)}
          label={item?.label}
          selectType={isMultiple ? 'checkbox' : 'radio'}
          value={$isSelected}
          disabled={disabled}
          className={cn(
            'focus:z-10 first:rounded-t last:rounded-b',
            $className
          )}
          isBorder
          {...rest}
          {...item}
        />
      );
    });

  return (
    <div
      className={cn(
        'overflow-hidden transition-opacity transform ease-in-out duration-150 flex flex-col bg-primitive-white rounded',
        {
          'shadow-tooltip origin-top z-50 absolute': isAbsolute,
          'pr-ds-4': hasScroll,
          '!h-0 overflow-auto opacity-0': !isOpen,
          'opacity-100 border border-default': isOpen,
        },
        className
      )}
      style={{
        width,
      }}
    >
      {isSearchable && (
        <div className="p-ds-12 -mr-ds-4 border-b border-default">
          <FormInput
            label={label}
            placeholder={placeholder}
            prefix={<GoSearch className="h-4 w-4" />}
            value={searchValue}
            onChange={onSearch}
            name={name}
          />
        </div>
      )}

      <div
        className={cn(
          'flex flex-col scrollbar flex-1 divide-y overflow-y-auto'
        )}
        style={{
          maxHeight,
        }}
        ref={ref}
        onScroll={handleOnScroll}
      >
        {options?.length <= 0 ? (
          emptyElement({ emptyElementClassName })
        ) : (
          <>
            {isGroup ? (
              <>
                {options?.map((item) => {
                  const { child } = item || [];
                  const {
                    className: $className,
                    textProps,
                    ...rest
                  } = menuItemProps || {};

                  if (child.length > 0) {
                    return (
                      <React.Fragment key={item?.value}>
                        <MenuItem
                          label="HEADER"
                          readOnly
                          textProps={{
                            color: 'muted',
                            type: 'overline',
                            ...textProps,
                          }}
                          className={cn('first:rounded-t', $className)}
                          {...rest}
                          {...item}
                        />
                        <div>{renderList(item?.child)}</div>
                      </React.Fragment>
                    );
                  }
                  return null;
                })}
              </>
            ) : (
              renderList(options)
            )}
          </>
        )}
      </div>
    </div>
  );
}

Menu.defaultProps = {
  maxHeight: '243px',
  options: [],
  value: [],
  isMultiple: false,
  isGroup: false,
  onChange: false,
  disabled: false,
  width: '100%',
  isAbsolute: true,
  isOpen: true,
  isSearchable: false,
  name: '',
  label: '',
  placeholder: '',
  searchValue: '',
  className: '',
  onSearch: () => {},
  menuItemProps: {},
  emptyElementClassName: '',
  emptyElement: ({ emptyElementClassName, ...rest }) => (
    <div
      {...rest}
      className={cn(
        'p-4 bg-primitive-white rounded cursor-default',
        emptyElementClassName
      )}
    >
      <Text
        type="caption"
        weight="medium"
        color="muted"
        className="text-center"
      >
        No items found
      </Text>
    </div>
  ),
  clickOutsideProps: false,
};

Menu.propTypes = {
  maxHeight: PropTypes.string,
  placeholder: PropTypes.string,
  searchValue: PropTypes.string,
  emptyElementClassName: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  emptyElement: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  isAbsolute: PropTypes.bool,
  isOpen: PropTypes.bool,
  isSearchable: PropTypes.bool,
  disabled: PropTypes.bool,
  isGroup: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.instanceOf(Object),
      ]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      onChange: PropTypes.instanceOf(Function),
      // menu item props extend
    })
  ),
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(Object),
  ]),
  isMultiple: PropTypes.bool,
  onSearch: PropTypes.instanceOf(Function),
  onChange: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  width: PropTypes.string,
  clickOutsideProps: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      ref: PropTypes.instanceOf(Object),
      set: PropTypes.instanceOf(Function),
    }),
  ]),
  menuItemProps: PropTypes.instanceOf(Object),
};

export default Menu;
